import { app, authentication } from "@microsoft/teams-js";
import { AuthStartPath } from "App";
import { appInsights } from "libs/app-insights";
import TeamsAuthTokenTimeoutError from "libs/teams/TeamsAuthTokenTimeoutError";
import VError from "verror";

const sdkInitializeTimeout = 1000 * 60;
const defaultGetAuthTokenTimeout = 1000 * 30;

// 일부 Method의 경우 함수 내부적으로 initializeTeamsSDK() 이후 사용하도록 코딩되어 있는데
// <AuthEndPage />, <AuthStartPage />의 경우 명시적으로 initializeTeamsSDK()를 호출하지 않아
// 의도적으로 코딩한 부분입니다.
// TODO: 추후 더 안전하게 코드를 줄일 수 있는 방법이 있을지 리서치합니다.

/**
 * Teams SDK를 초기화합니다.
 */
export function initializeTeamsSDK(): Promise<void> {
  const initializeTeamsSDKStartTime = Date.now();

  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(`${sdkInitializeTimeout}ms 타임아웃으로 Teams SDK 초기화 실패`);
    }, sdkInitializeTimeout);

    app.initialize()
      .then(() => {
        clearTimeout(timeoutId);
        const initializeTeamsSDKEndTime = Date.now();

        appInsights.trackEvent({
          name: "InitializeTeamsSDK",
          properties: {
            Duration: initializeTeamsSDKEndTime - initializeTeamsSDKStartTime,
          },
        });

        resolve();
      });
  });
}

/**
 * 팀즈 컨텍스트를 가져옵니다.
 */
export function getTeamsContext(): Promise<app.Context> {
  const getTeamsContextStartTime = Date.now();
  return new Promise<app.Context>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(`${sdkInitializeTimeout}ms 타임아웃으로 Teams Context 가져오기 실패`);
    }, sdkInitializeTimeout);

    app.getContext()
      .then(context => {
        clearTimeout(timeoutId);

        // app Insight에 사용자 정보를 남기기
        // https://docs.microsoft.com/ko-kr/azure/azure-monitor/app/api-custom-events-metrics#authenticated-users
        appInsights.setAuthenticatedUserContext(context.user?.userPrincipalName ?? "사용자 UPN 정보가 없음");

        const getTeamsContextEndTime = Date.now();

        appInsights.trackEvent({
          name: "GetTeamsContext",
          properties: {
            ...context,
            Duration: getTeamsContextEndTime - getTeamsContextStartTime,
            WindowInnerWidth: window.innerWidth,
            WindowInnerHeight: window.innerHeight,
          },
        });

        resolve(context);
      });
  });
}

/**
 * 테마 변경 핸들러를 등록합니다.
 * @param handler 테마 변경 핸들러
 */
export function registerOnThemeChangeHandler(handler: (theme: string) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    initializeTeamsSDK()
      .then(() => {
        app.registerOnThemeChangeHandler(handler);
        resolve();
      })
      .catch((err) => reject(err));
  });
}

/**
 * 사용자 상호작용 방식으로 인증합니다. (주로 사용자 권한 동의를 위하여 사용됨)
 * @param url (예시: https://login.microsoftonline.com/ac78b31e-af16-426e-844f-658d97c754ca//oauth2/v2.0/authorize?client_id=45695dd7-90c2-4e56-acff-efd5247265be&response_type=code&redirect_uri=https://localhost:5001/signin-oidc&response_mode=query&scope=offline_access%20user.read)
 * @returns 인증 결과(인증 코드 또는 토큰)
 */
export function authenticate(url?: string): Promise<string> {
  const consentUri: string = url ? `?url=${encodeURIComponent(url)}` : "";
  const authStartEndPoint = `${window.location.origin}${AuthStartPath}${consentUri}`;

  // SDK 메소드 공식 문서:
  // https://docs.microsoft.com/en-us/microsoftteams/platform/tabs/how-to/authentication/auth-tab-aad#initiate-authentication-flow
  return new Promise((resolve, reject) => {
    authentication.authenticate({
      url: authStartEndPoint,
      width: 600,
      height: 535,
    })
      .then(result => {
        const data = localStorage.getItem(result) ?? "";
        localStorage.removeItem(result);
        const tokenResult = JSON.parse(data);
        const accessToken = tokenResult?.accessToken;

        if (accessToken) {
          resolve(accessToken);
        } else {
          reject(`대화형 로그인 실패 (예: 팝업에서 인증을 하지 않음)`);
        }
      })
      .catch(reason => reject(reason));
  });
}

/**
 * 인증 성공을 알립니다. 인증을 위해 별도로 떴던 창은 닫힙니다.
 * @param result
 * @returns
 */
export function notifyAuthSuccess(result?: string): Promise<void> {
  return new Promise((resolve) => {
    initializeTeamsSDK()
      .then(() => {
        authentication.notifySuccess(result);
        resolve();
      });
  });
}

/**
 * 인증 실패를 알립니다. 인증을 위해 별도로 떴던 창은 닫힙니다.
 * @param reason
 * @returns
 */
export function notifyAuthFailure(reason?: string): Promise<void> {
  return new Promise((resolve) => {
    initializeTeamsSDK()
      .then(() => {
        authentication.notifyFailure(reason);
        resolve();
      });
  });
}

/**
 * Teams AuthToken을 확보합니다.
 */
export async function getTeamsToken(): Promise<string> {
  const teamsGetAuthTokenStartTime = Date.now();

  const token = await new Promise<string>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new TeamsAuthTokenTimeoutError(`${defaultGetAuthTokenTimeout}ms 타임아웃으로 Teams AuthToken 확보 실패`));
    }, defaultGetAuthTokenTimeout);

    authentication.getAuthToken()
      .then(authToken => {
        clearTimeout(timeoutId);
        resolve(authToken);
      })
      .catch(reason => {
        clearTimeout(timeoutId);
        reject(new VError(new Error(reason), `Teams AuthToken 확보 실패`));
      });
  });

  const teamsGetAuthTokenEndTime = Date.now();

  appInsights.trackEvent({
    name: "TeamsGetAuthToken",
    properties: {
      Duration: teamsGetAuthTokenEndTime - teamsGetAuthTokenStartTime,
    },
  });

  return token;
}
